// src/utils/authentication/dashboardAccess.js
/**
 * Dashboard access-control predicates (DASH-AUTH-1).
 *
 * The authoritative source of "is this user allowed on the dashboard" is the
 * backend User Service, surfaced via the gateway `GET /v1/users/me` response
 * (`accountContext.dashboardMembership*`). Keeping this logic in one place means
 * the route guard, nav, and tests all agree on a single definition.
 *
 * The backend User Service derives `dashboardMembershipStatus` from the verified
 * identity (`deriveDashboardMembershipStatus` in user-context-store):
 *   • `active`         — user has a `dashboard-*` role claim.
 *   • `provisioned`    — user has an organisation id/role.
 *   • `not_provisioned`— neither (e.g. a plain patient account). → denied.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * INTERIM CLAIMS BRIDGE — remove once DASH-AUTH-2/3 provisioning lands.
 * ──────────────────────────────────────────────────────────────────────────
 * The backend grants `active` for `dashboard-*` role claims, but the existing
 * invite/role Cloud Functions still set the *legacy* singular `role` claim
 * (`clinician`/`admin`) and recognise a super-admin by email — neither of which
 * the backend maps yet. Until those functions are updated to issue `dashboard-*`
 * roles (or org membership is provisioned), we also honour the legacy Firebase
 * claims so existing clinicians/admins keep access. Set `INTERIM_CLAIMS_BRIDGE`
 * to `false` (or delete the bridge) once provisioning is the sole source.
 */

/** Backend membership statuses that grant dashboard access. */
export const AUTHORISED_MEMBERSHIP_STATUSES = new Set(["active", "provisioned"]);

/** Legacy Firebase `claims.role` values that grant access (interim bridge). */
export const DASHBOARD_ROLES = new Set(["clinician", "admin", "super_admin"]);

/** Prefix the backend uses for dashboard role claims (e.g. `dashboard-admin`). */
export const BACKEND_DASHBOARD_ROLE_PREFIX = "dashboard-";

/** Roles with elevated (admin) dashboard privileges. */
export const DASHBOARD_ADMIN_ROLES = new Set(["admin", "super_admin"]);

/** Toggle the interim Firebase-claims bridge described above. */
export const INTERIM_CLAIMS_BRIDGE = true;

/**
 * Normalises the role(s) reported by the backend user-context payload.
 *
 * @param {object|null|undefined} context - `/v1/users/me` payload.
 * @returns {string[]}
 */
export function getBackendRoles(context) {
  const roles = context?.user?.roles;
  return Array.isArray(roles) ? roles : [];
}

/**
 * Whether the (still-loading-tolerant) Firebase claims indicate dashboard access.
 *
 * @param {object|null|undefined} claims - Firebase custom claims.
 * @param {string|null|undefined} email  - Authenticated user's email.
 * @returns {boolean}
 */
function claimsGrantAccess(claims) {
  if (!INTERIM_CLAIMS_BRIDGE) return false;
  return DASHBOARD_ROLES.has(claims?.role);
}

/**
 * Authoritative dashboard-access decision.
 *
 * @param {object|null|undefined} context - `/v1/users/me` payload.
 * @param {{ claims?: object|null, email?: string|null }} [bridge] - Firebase
 *   claims fallback used only while backend provisioning is unavailable.
 * @returns {boolean}
 */
export function isDashboardAuthorised(context, { claims } = {}) {
  const account = context?.accountContext;

  if (account) {
    // Regions/configs that don't gate the dashboard on membership.
    if (account.dashboardMembershipRequired === false) return true;
    // Provisioned, active membership — the long-term source of truth.
    if (AUTHORISED_MEMBERSHIP_STATUSES.has(account.dashboardMembershipStatus)) {
      return true;
    }
  }

  // Backend-reported roles: any `dashboard-*` role grants access (these are
  // also what the backend folds into an `active` membership status).
  if (
    getBackendRoles(context).some(
      (role) =>
        role?.startsWith?.(BACKEND_DASHBOARD_ROLE_PREFIX) ||
        DASHBOARD_ROLES.has(role),
    )
  ) {
    return true;
  }

  // Interim Firebase-claims bridge (see file header).
  return claimsGrantAccess(claims);
}

/**
 * Whether the user belongs to an organisation that should be allowed to view
 * organisation-scoped study / clinic records.
 *
 * @param {object|null|undefined} context - `/v1/users/me` payload.
 * @returns {boolean}
 */
export function hasDashboardOrganisationMembership(context) {
  const account = context?.accountContext;

  return Boolean(account?.organisationId) &&
    AUTHORISED_MEMBERSHIP_STATUSES.has(account?.dashboardMembershipStatus);
}

/**
 * Whether the user holds an active/provisioned admin membership in an
 * organisation, as distinct from bootstrap/developer access.
 *
 * @param {object|null|undefined} context - `/v1/users/me` payload.
 * @returns {boolean}
 */
export function hasDashboardOrganisationAdminMembership(context) {
  const account = context?.accountContext;

  return Boolean(account?.organisationId) &&
    AUTHORISED_MEMBERSHIP_STATUSES.has(account?.dashboardMembershipStatus) &&
    account?.organisationRole === "admin";
}

/**
 * Whether the user has elevated/admin dashboard privileges.
 *
 * @param {object|null|undefined} context - `/v1/users/me` payload.
 * @param {{ claims?: object|null, email?: string|null }} [bridge]
 * @returns {boolean}
 */
export function isDashboardAdmin(context, { claims } = {}) {
  const account = context?.accountContext;

  if (
    AUTHORISED_MEMBERSHIP_STATUSES.has(account?.dashboardMembershipStatus) &&
    DASHBOARD_ADMIN_ROLES.has(account?.organisationRole)
  ) {
    return true;
  }

  if (
    getBackendRoles(context).some(
      (role) =>
        role === `${BACKEND_DASHBOARD_ROLE_PREFIX}admin` ||
        role === `${BACKEND_DASHBOARD_ROLE_PREFIX}super_admin` ||
        DASHBOARD_ADMIN_ROLES.has(role),
    )
  ) {
    return true;
  }
  if (!INTERIM_CLAIMS_BRIDGE) return false;
  return DASHBOARD_ADMIN_ROLES.has(claims?.role);
}
